import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import NewRequestDialog from "@/components/NewRequestDialog";
import { Plus, Search, Inbox, CheckCircle2, XCircle, Clock, ArrowRight, Archive, ArchiveRestore, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { LeavePeriod } from "@/components/requests/LeavePeriod";
import { useAuth, isStudent } from "@/lib/auth";

const CAT_LABEL = {
  permesso_assenza: "Permesso assenza",
  visita_medica: "Visita medica / Sanità",
  certificato: "Certificato",
  prenotazione_esame: "Prenotazione esame",
  colloquio: "Colloquio",
  generica: "Generica",
};

const STATUS_STYLE = {
  pending:    { label: "In attesa",    cls: "bg-amber-50 text-amber-800 border-amber-200",     icon: Clock },
  approvato:  { label: "Approvata",    cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2 },
  rifiutato:  { label: "Rifiutata",    cls: "bg-red-50 text-red-800 border-red-200",           icon: XCircle },
};

export default function Requests() {
  const { user } = useAuth();
  const student = isStudent(user);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [status, setStatus] = useState("all");
  const [category, setCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [mine, setMine] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["requests", { status, category, search, showArchived, mine }],
    queryFn: async () => {
      const params = {};
      if (mine) params.assigned_to = "me";
      if (status !== "all") params.status = status;
      if (category !== "all") params.category = category;
      if (search) params.search = search;
      if (showArchived) params.archived = true;
      return (await api.get("/requests", { params })).data;
    },
    refetchInterval: 30_000,
  });
  const { data: stats } = useQuery({
    queryKey: ["requests-stats"],
    queryFn: async () => (await api.get("/requests-stats/overview")).data,
    enabled: !student,
  });

  const archiveMut = useMutation({
    mutationFn: async ({ id, archive }) => (
      await api.patch(`/requests/${id}/${archive ? "archive" : "unarchive"}`)
    ).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["requests"] });
      qc.invalidateQueries({ queryKey: ["requests-stats"] });
      qc.invalidateQueries({ queryKey: ["requests-stats-badge"] });
      toast.success(vars.archive ? "Richiesta archiviata" : "Ripristinata");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  const basePath = student ? "/portale/richieste" : "/richieste";

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={student ? "Le mie richieste" : "Richieste alla segreteria"}
        subtitle={student
          ? "Invia richieste alla segreteria didattica: permessi, certificati, prenotazioni esami e colloqui con i docenti."
          : "Approva o rifiuta le richieste degli studenti. Puoi anche creare richieste per conto di uno studente."}
        actions={
          <Button
            data-testid="request-new-btn"
            onClick={() => setCreateOpen(true)}
            className="rounded-full bg-[#1C352D] hover:bg-[#264A3E]"
          >
            <Plus size={16} className="mr-1.5"/> Nuova richiesta
          </Button>
        }
      />

      {!student && stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Kpi label="Totale" value={stats.total} icon={Inbox}/>
          <Kpi label="In attesa" value={stats.pending} icon={Clock} color="text-amber-700"/>
          <Kpi label="Approvate" value={stats.approvate} icon={CheckCircle2} color="text-emerald-700"/>
          <Kpi label="Rifiutate" value={stats.rifiutate} icon={XCircle} color="text-red-700"/>
        </div>
      )}

      <Card className="border border-stone-200 rounded-xl shadow-none">
        <div className="p-4 flex flex-col md:flex-row gap-3 border-b border-stone-100">
          <div className="relative flex-1 max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
            <Input
              data-testid="request-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca oggetto o descrizione…"
              className="pl-9"
            />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40" data-testid="request-status-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pending">In attesa</SelectItem>
              <SelectItem value="approvato">Approvate</SelectItem>
              <SelectItem value="rifiutato">Rifiutate</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-56" data-testid="request-cat-filter"><SelectValue placeholder="Categoria"/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le categorie</SelectItem>
              {Object.entries(CAT_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button
            variant={showArchived ? "default" : "outline"}
            data-testid="request-archived-toggle"
            onClick={() => setShowArchived((v) => !v)}
            className={`rounded-full ${showArchived ? "bg-stone-700 hover:bg-stone-800 text-white" : ""}`}
          >
            <Archive size={13} className="mr-1.5"/> {showArchived ? "Archiviate" : "Attive"}
          </Button>
          {!student && (
            <Button
              variant={mine ? "default" : "outline"}
              data-testid="request-mine-toggle"
              onClick={() => setMine((v) => !v)}
              className={`rounded-full ${mine ? "bg-[#1C352D] hover:bg-[#264A3E] text-white" : ""}`}
            >
              <UserCheck size={13} className="mr-1.5"/> Assegnate a me
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="py-10 text-center text-stone-400 text-sm">Caricamento…</div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-stone-400 text-sm">
            Nessuna richiesta. {student && "Crea la prima con \"Nuova richiesta\"."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {items.map((r, i) => {
              const st = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
              const StIcon = st.icon;
              return (
                <li
                  key={r.id}
                  data-testid={`request-row-${r.id}`}
                  className="p-4 md:p-5 flex items-start gap-4 hover:bg-stone-50/70 transition-colors animate-in-row group"
                  style={{ animationDelay: `${i * 15}ms` }}
                >
                  <div
                    onClick={() => navigate(`${basePath}/${r.id}`)}
                    className={`w-10 h-10 rounded-full border grid place-items-center shrink-0 cursor-pointer ${st.cls}`}
                  >
                    <StIcon size={16}/>
                  </div>
                  <div
                    onClick={() => navigate(`${basePath}/${r.id}`)}
                    className="min-w-0 flex-1 cursor-pointer"
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium">{r.subject}</div>
                      <Badge className="bg-stone-100 text-stone-700 border-0 text-[10px] rounded-full">{r.medical ? CAT_LABEL.visita_medica : (CAT_LABEL[r.category] || r.category)}</Badge>
                      {r.attachments?.length > 0 && <Badge className="bg-sky-50 text-sky-800 border border-sky-200 text-[10px] rounded-full">{r.attachments.length} allegat{r.attachments.length > 1 ? "i" : "o"}</Badge>}
                      <Badge className={`text-[10px] rounded-full border ${st.cls}`}>{st.label}</Badge>
                      {r.archived && <Badge className="bg-stone-200 text-stone-700 text-[10px] rounded-full">ARCHIVIATA</Badge>}
                      {!student && r.assigned_to_name && <Badge className="bg-amber-50 text-amber-800 border border-amber-200 text-[10px] rounded-full" data-testid={`request-assigned-${r.id}`}>Assegnata a {r.assigned_to_name}</Badge>}
                    </div>
                    {r.date_from && <div className="text-[12px] mt-1"><LeavePeriod req={r} compact /></div>}
                    <div className="text-[12px] text-stone-500 mt-1">
                      {!student && r.student_name && <>👤 {r.student_name} · {r.student_matricola} · </>}
                      Creata {fmtDate(r.created_at)}
                      {r.messages_count > 0 && <> · 💬 {r.messages_count} messaggi</>}
                    </div>
                    {r.description && (
                      <div className="text-sm text-stone-600 mt-1 truncate max-w-[600px]">{r.description}</div>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); archiveMut.mutate({ id: r.id, archive: !r.archived }); }}
                    data-testid={`request-archive-${r.id}`}
                    title={r.archived ? "Ripristina" : "Archivia"}
                    className="opacity-0 group-hover:opacity-100 transition-opacity p-2 rounded hover:bg-stone-100 text-stone-500 hover:text-[#1C352D] self-center"
                  >
                    {r.archived ? <ArchiveRestore size={15}/> : <Archive size={15}/>}
                  </button>
                  <ArrowRight size={14} className="text-stone-400 self-center"/>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <NewRequestDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(d) => navigate(`${basePath}/${d.id}`)}
      />
    </div>
  );
}

function Kpi({ label, value, icon: Icon, color = "text-[#1C352D]" }) {
  return (
    <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
      <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Icon size={12}/> {label}</div>
      <div className={`text-2xl font-semibold mt-1 ${color}`}>{value ?? 0}</div>
    </Card>
  );
}
